import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../hooks/useAuth';
import { useFirebase } from '../../../hooks/useFirebase';
import { queryDelete, queryGet, queryPost, queryPut } from '../../tools/tools';

const ENDPOINT = '/coupons';
const KEY = 'coupons';

export const useCoupons = () => {
  const { user } = useAuth();
  const { remoteConfig } = useFirebase();
  const queryClient = useQueryClient();

  const queryKey = [KEY, user?.uid];

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => queryGet(remoteConfig, ENDPOINT, user?.accessToken),
    enabled: !!user?.accessToken && !!remoteConfig
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const createMutation = useMutation({
    mutationFn: ({ coupon, consultantId }) =>
      queryPost(remoteConfig, ENDPOINT, { ...coupon, consultantId: consultantId ?? null }, user?.accessToken),
    onSuccess: invalidate
  });

  const updateMutation = useMutation({
    mutationFn: ({ coupon, consultantId }) =>
      queryPut(remoteConfig, ENDPOINT, { ...coupon, consultantId: consultantId ?? null }, user?.accessToken),
    onSuccess: invalidate
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => queryDelete(remoteConfig, ENDPOINT, id, user?.accessToken),
    onSuccess: invalidate
  });

  return {
    coupons: data ?? [],
    isLoading,
    isError,
    refetch,
    create: createMutation.mutate,
    isCreating: createMutation.isPending,
    update: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    remove: deleteMutation.mutate,
    isRemoving: deleteMutation.isPending
  };
};

export const useCouponRedemptions = (couponId) => {
  const { user } = useAuth();
  const { remoteConfig } = useFirebase();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['coupon-redemptions', couponId, user?.uid],
    queryFn: () => queryGet(remoteConfig, `/coupons/${couponId}/redemptions`, user?.accessToken),
    enabled: !!couponId && !!user?.accessToken && !!remoteConfig
  });

  return { redemptions: data ?? [], isLoading, refetch };
};
